import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { PostHogProvider, usePostHog } from "posthog-js/react";

/**
 * Analytics gating — only enabled on production deployment at layerly.com.au.
 * Local dev preview (preview.emergentagent.com / localhost) is intentionally excluded
 * so test traffic doesn't pollute PostHog.
 */
const isProductionAnalytics = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "layerly.com.au" || host.endsWith(".layerly.com.au");
};

let initialized = false;
if (typeof window !== "undefined" && isProductionAnalytics()) {
  const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
  if (apiKey) {
    posthog.init(apiKey, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
      defaults: "2026-01-30",
      capture_pageview: false, // we drive pageviews manually from RoutePageviewTracker
      autocapture: true,
      person_profiles: "always",
      loaded: () => {
        initialized = true;
      },
    });
  }
}

/** Fires a $pageview event on every react-router pathname change. */
const RoutePageviewTracker = () => {
  const location = useLocation();
  const ph = usePostHog();

  useEffect(() => {
    if (!ph) return;
    ph.capture("$pageview", {
      $current_url: window.location.href,
      route_path: location.pathname + location.search,
    });
  }, [location.pathname, location.search, ph]);

  return null;
};

export const AnalyticsProvider = ({ children }) => {
  if (!isProductionAnalytics() || !process.env.REACT_APP_POSTHOG_API_KEY) {
    return <>{children}</>;
  }
  return (
    <PostHogProvider client={posthog}>
      <RoutePageviewTracker />
      {children}
    </PostHogProvider>
  );
};

// Silence "initialized" unused-variable lint without removing the side-effect indicator
// eslint-disable-next-line no-unused-vars
const _initFlag = () => initialized;
